import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  InputNumber,
  Row,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from 'antd';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { formatIstDateTime } from '@/utils/analyticsWindow';
import type { ChallengerSettings } from './challengeForm';

const RARITIES = ['Amateur', 'Semi-pro', 'Pro', 'World class'];

/** Streak rewards per day, Day 3 / Day 7 crates, retry rule. Applies to every challenge without an override. */
export const SettingsTab: React.FC = () => {
  const [s, setS] = useState<ChallengerSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    adminApi
      .challengerSettings()
      .then((r) => setS(r.data))
      .catch((e) => message.error(apiErrorMessage(e, 'Could not load the settings')));
  }, []);

  if (!s) return null;

  const setDay = (day: number, field: 'coins' | 'matchTokens', v: number | null) =>
    setS({
      ...s,
      dayRewards: s.dayRewards.map((d) => (d.day === day ? { ...d, [field]: v ?? 0 } : d)),
    });
  const setMilestone = (day: number, patch: Partial<ChallengerSettings['milestones'][number]>) =>
    setS({ ...s, milestones: s.milestones.map((m) => (m.day === day ? { ...m, ...patch } : m)) });

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const body = {
        streakLength: s.streakLength,
        dayRewards: s.dayRewards,
        milestones: s.milestones,
        retry: s.retry,
        freeAttempts: s.freeAttempts,
        streakEnabled: s.streakEnabled !== false,
      };
      setS((await adminApi.challengerSaveSettings(body)).data);
      message.success('Settings saved; they apply to the next success');
    } catch (e) {
      setErrors(
        (e as { response?: { data?: { errors?: string[] } } }).response?.data?.errors ?? []
      );
      message.error(apiErrorMessage(e, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {errors.length > 0 && <Alert type="error" showIcon message={errors.join('; ')} />}
      <Space>
        Streak
        <Switch
          checked={s.streakEnabled !== false}
          onChange={(v) => setS({ ...s, streakEnabled: v })}
        />
        <Typography.Text type="secondary">
          {s.streakEnabled !== false
            ? 'On: rewards follow the streak day, with the Day 3 and Day 7 milestones.'
            : 'Off: every success pays the Day 1 reward, no milestones, and players keep the streak they have.'}
        </Typography.Text>
      </Space>
      <Card title={`Reward per streak day (streak of ${s.streakLength})`} size="small">
        <Table
          size="small"
          rowKey="day"
          pagination={false}
          dataSource={s.dayRewards}
          columns={[
            { title: 'Day', dataIndex: 'day' },
            {
              title: 'Coins',
              dataIndex: 'coins',
              render: (v, d) => (
                <InputNumber
                  min={0}
                  max={100000}
                  value={v}
                  onChange={(x) => setDay(d.day, 'coins', x)}
                />
              ),
            },
            {
              title: 'Match tokens',
              dataIndex: 'matchTokens',
              render: (v, d) => (
                <InputNumber
                  min={0}
                  max={100000}
                  value={v}
                  onChange={(x) => setDay(d.day, 'matchTokens', x)}
                />
              ),
            },
          ]}
        />
      </Card>
      <Row gutter={16}>
        {s.milestones.map((m) => (
          <Col span={12} key={m.day}>
            <Card title={`Day ${m.day} crate`} size="small">
              {m.coins !== undefined && (
                <Space>
                  Coins
                  <InputNumber
                    min={1}
                    max={100000}
                    value={m.coins}
                    onChange={(v) => setMilestone(m.day, { coins: v ?? 0 })}
                  />
                </Space>
              )}
              {m.cards && (
                <Space direction="vertical">
                  <Space>
                    Cards
                    <InputNumber
                      min={1}
                      max={10}
                      value={m.cards.count}
                      onChange={(v) =>
                        setMilestone(m.day, { cards: { ...m.cards!, count: v ?? 1 } })
                      }
                    />
                  </Space>
                  <Typography.Text type="secondary">Rarity weights (relative)</Typography.Text>
                  {RARITIES.map((name, r) => (
                    <Space key={name}>
                      <span style={{ width: 90, display: 'inline-block' }}>{name}</span>
                      <InputNumber
                        min={0}
                        max={1000}
                        value={m.cards!.rarityWeights[String(r)] ?? 0}
                        onChange={(v) =>
                          setMilestone(m.day, {
                            cards: {
                              ...m.cards!,
                              rarityWeights: { ...m.cards!.rarityWeights, [String(r)]: v ?? 0 },
                            },
                          })
                        }
                      />
                    </Space>
                  ))}
                </Space>
              )}
            </Card>
          </Col>
        ))}
      </Row>
      <Card title="Attempts" size="small">
        <Space size="large">
          <Space>
            Free attempts
            <InputNumber
              min={1}
              max={3}
              value={s.freeAttempts}
              onChange={(v) => setS({ ...s, freeAttempts: v ?? 1 })}
            />
          </Space>
          <Space>
            Ad retry
            <Switch
              checked={s.retry.enabled}
              onChange={(v) => setS({ ...s, retry: { ...s.retry, enabled: v } })}
            />
          </Space>
          <Space>
            Retries
            <InputNumber
              min={0}
              max={3}
              value={s.retry.limit}
              onChange={(v) => setS({ ...s, retry: { ...s.retry, limit: v ?? 0 } })}
            />
          </Space>
        </Space>
      </Card>
      <Space>
        <Button type="primary" loading={saving} onClick={save}>
          Save settings
        </Button>
        {s.updatedAt && (
          <Typography.Text type="secondary">
            Last saved {formatIstDateTime(new Date(s.updatedAt))} IST
            {s.updatedBy ? ` by ${s.updatedBy}` : ''}
          </Typography.Text>
        )}
      </Space>
    </Space>
  );
};
